import type {
  BusinessType,
  Heat,
  HideReason,
  Language,
  ObjectionType,
  Outcome,
  OwnerRole,
  Persona,
  PlanGapReason,
  PlaybookVariant,
  PosSystem,
  ScorePart,
  SignalBucket,
  SignalEvent,
  SignalKind,
  SignalSource,
  SpokeWith,
  Stage,
  TouchChannel,
  TriageFlag,
} from '../../../shared/enums';

export const STAGE_LABELS: Record<Stage, string> = {
  new: 'New',
  visited: 'Visited',
  qualified: 'Qualified',
  demo: 'Demo',
  proposal: 'Proposal',
  closed_won: 'Won',
  closed_lost: 'Lost',
};

export const HEAT_LABELS: Record<Heat, string> = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  restaurant: 'Restaurant',
  cafe: 'Café',
  pizzeria: 'Pizzeria',
  bar: 'Bar',
  retail: 'Retail',
  salon: 'Salon',
  other: 'Other',
};

export const OWNER_ROLE_LABELS: Record<OwnerRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  partner: 'Partner',
};

/** How a touch happened (D90). */
export const CHANNEL_LABELS: Record<TouchChannel, string> = {
  walk_in: 'Walk-in',
  call: 'Call',
  voicemail: 'Voicemail',
  email: 'Email',
  linkedin: 'LinkedIn',
  meeting: 'Meeting',
  other: 'Other',
};

/** The same, counted: "2 walk-ins · 1 call". */
export const CHANNEL_PLURALS: Record<TouchChannel, string> = {
  walk_in: 'walk-ins',
  call: 'calls',
  voicemail: 'voicemails',
  email: 'emails',
  linkedin: 'LinkedIn notes',
  meeting: 'meetings',
  other: 'other touches',
};

/** The message library's variants, by what they are for (D88). */
export const PLAYBOOK_VARIANT_LABELS: Record<PlaybookVariant, string> = {
  pre_open: 'Pre-open',
  switch: 'Competitive switch',
  no_hook: 'No hook',
  pain: 'Pain signal',
  voicemail: 'Voicemail',
  voicemail_q4: 'Voicemail · Nov–Dec',
  gatekeeper: 'Whoever answers',
  walk_in_owner: 'Walk-in · owner there',
  walk_in_owner_q4: 'Walk-in · Nov–Dec',
  walk_in_staff: 'Walk-in · owner out',
  card_note: 'Note on the card',
  linkedin: 'LinkedIn note',
  linkedin_connected: 'LinkedIn message',
  follow_up_2: 'Follow-up 2 · the one-pager',
  follow_up_3_pre_open: 'Follow-up 3 · pre-open',
  follow_up_3: 'Follow-up 3 · established',
  follow_up_4: 'Follow-up 4 · the cheap question',
  follow_up_5: 'Follow-up 5 · close the loop',
};

/** The four personas the message library is written for (D88). */
export const PERSONA_LABELS: Record<Persona, string> = {
  restaurant: 'Restaurant',
  retail: 'Retail',
  salon: 'Salon',
  counter: 'Counter',
};

export const LANGUAGE_LABELS: Record<Language, string> = { en: 'EN', es: 'ES', pt: 'PT' };
export const LANGUAGE_NAMES: Record<Language, string> = { en: 'English', es: 'Español', pt: 'Português' };

export const POS_LABELS: Record<PosSystem, string> = {
  toast: 'Toast',
  clover: 'Clover',
  spoton: 'SpotOn',
  touchbistro: 'TouchBistro',
  lightspeed: 'Lightspeed',
  shopify: 'Shopify',
  chownow: 'ChowNow',
  aloha: 'Aloha',
  micros: 'Micros',
  square: 'Square',
  cash_only: 'Cash only',
  other: 'Other',
  unknown: 'Unknown',
};

export const SPOKE_WITH_LABELS: Record<SpokeWith, string> = {
  owner: 'Owner',
  manager: 'Manager',
  gatekeeper: 'Gatekeeper',
  nobody: 'Nobody',
};

export const OUTCOME_LABELS: Record<Outcome, string> = {
  interested: 'Interested',
  follow_up: 'Follow up',
  not_interested: 'Not interested',
  asked_to_return: 'Come back later',
  closed: 'Closed',
};

export const OBJECTION_LABELS: Record<ObjectionType, string> = {
  price: 'Price',
  contract_lock: 'Contract lock',
  missing_feature: 'Missing feature',
  loyal_to_current_rep: 'Loyal to rep',
  partner_decides: 'Partner decides',
  just_switched: 'Just switched',
  other: 'Other',
};

export const money = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/** New-business signals (D57). */
export const SIGNAL_EVENT_LABELS: Record<SignalEvent, string> = {
  new_business: 'New license',
  new_owner: 'New owner',
  moving: 'New location',
  remodel: 'Remodel',
};

export const SIGNAL_KIND_LABELS: Record<SignalKind, string> = {
  food_drink: 'Food & drink',
  retail: 'Retail',
  food_truck: 'Food trucks',
  service: 'Services',
};

/** The kind of one business, on its card (D72). */
export const SIGNAL_KIND_SINGULAR: Record<SignalKind, string> = {
  food_drink: 'Restaurant',
  retail: 'Store',
  food_truck: 'Food truck',
  service: 'Service',
};

/** Why the sort hid a business (D72), as its card says it. */
/** What the sort could not settle, as a caveat on the why line (D75). */
export const TRIAGE_FLAG_LABELS: Record<TriageFlag, string> = {
  city_unverified: 'city unverified',
};

export const HIDE_REASON_LABELS: Record<HideReason, string> = {
  chain: 'chain',
  outside_oc: 'outside Orange County',
  already_square: 'already on Square',
  rep: 'Hidden by you',
  later: 'Later',
};

/** The parts of a score, as the why line says them (D72). */
export const SCORE_PART_LABELS: Record<ScorePart, string> = {
  event: 'new storefront',
  kind: 'walk-in business',
  google_new: 'just opened on Google',
  google_open: 'open on Google',
  google_established: 'open for years',
  google_absent_contact: 'not on Google yet, reachable',
  google_absent: 'not on Google yet',
  pos: "on a competitor's POS",
  filed: 'filed recently',
  phone: 'has a phone',
  email: 'has an email',
  website: 'has a website',
  operator: 'several locations',
};

/** The sections of the New businesses page (D72). */
export const BUCKET_SECTION_TITLES: Record<Exclude<SignalBucket, 'merged'>, string> = {
  top: 'Worth a visit today',
  queue: 'More to review',
  hidden: 'Hidden: chains, duplicates, outside the county',
};
/** The section between them: the rest within three miles of the rep (D80). */
export const NEAR_SECTION_TITLE = 'Near you';

/** Under an empty New businesses list: the next batch comes on its own (D70). */
export const SIGNALS_EMPTY_HINT =
  'New licenses and permits arrive on their own, each source at its own hour of the day.';

/** The New businesses filters on the desktop (D66), and a business with no city in the City one. */
export const SIGNAL_FILTER_NAMES = {
  source: 'Source',
  type: 'Type',
  kind: 'Category',
  city: 'City',
} as const;
export const NO_CITY_LABEL = 'No city';

export const SIGNAL_SOURCE_LABELS: Record<SignalSource, string> = {
  abc: 'ABC liquor license',
  ochca: 'OC Health plan check',
  anaheim: 'Anaheim business license',
  huntington_beach: 'Huntington Beach business license',
  yorba_linda: 'Yorba Linda new-business list',
  la_habra: 'La Habra new-business list',
  costa_mesa: 'Costa Mesa new-business list',
  laguna_beach: 'Laguna Beach new-business list',
  irvine: 'Irvine business license',
  newport_beach: 'Newport Beach business license',
  lake_forest: 'Lake Forest business registration',
  fullerton: 'Fullerton business license',
  orange: 'Orange business license',
  tustin: 'Tustin business license',
  westminster: 'Westminster business license',
  brea: 'Brea business license',
  cypress: 'Cypress business license',
  fountain_valley: 'Fountain Valley business license',
  stanton: 'Stanton business license',
  seal_beach: 'Seal Beach business license',
  san_clemente: 'San Clemente business license',
  los_alamitos: 'Los Alamitos business license',
  fbn: 'County business-name filing',
  city_agendas: 'City planning agenda',
  anaheim_permits: 'Anaheim planning permit',
};

/** Plan tomorrow's marks on a stop (D82): each takes the place out of the pool. */
export const PLACE_MARK_LABELS: Record<'not_fit' | 'chain' | 'square', string> = {
  not_fit: 'Not a fit',
  chain: 'It’s a chain',
  square: 'Uses Square',
};

export const PLAN_GAP_REASON_LABELS: Record<PlanGapReason, string> = {
  rush: 'Peak hours: avoid busy visits',
  opening_hours: 'Opening hours: waiting for a full visit window',
  meeting: 'Waiting for the scheduled meeting',
  day_limit: 'Not enough time for another visit before day end',
};

export const scanStateLabel: Record<import('../../../shared/types').ScanState, string> = {
  new: 'Not looked up yet', running: 'Lookup running', incomplete: 'Partly looked up',
  interrupted: 'Interrupted lookup', complete: 'Looked up', stale: 'Needs a fresh lookup',
  'legacy-complete': 'Looked up before this version', 'legacy-unknown': 'Progress unknown',
};

/** The closed disclosure on each Areas card that keeps the scan counters one tap away (CAY-73). */
export const AREA_DETAILS_SUMMARY = 'Lookup details';

/** Offline with new businesses cached but no leads on this device (CAY-73). */
export const OFFLINE_SIGNALS_ONLY = {
  title: 'No leads on this device yet.',
  body: (savedAt: string) => `Showing new businesses saved ${savedAt}; your leads load when you are back online.`,
} as const;

/** The D110 ledger on the lead picker's status line: one line at 390px for real values on the
 *  $5 cap (CAY-126, CAY-120 finding 1; placePicker.css keeps two lines reserved for wider ones).
 *  Spend rounds up to the cent and unit costs up to a tenth of a cent, so it never reads lower
 *  than the server's conservative estimate; the cap rounds down for the same reason. */
export const pickerLedgerLabel = (s: {
  spent_micros: number;
  cap_micros: number;
  estimates: { autocomplete: number; details: number };
}): string => {
  const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const tenthCents = (micros: number) => `${Math.ceil(micros / 1e3) / 10}¢`;
  return (
    `${dollars(Math.ceil(s.spent_micros / 1e4))} of ${dollars(Math.floor(s.cap_micros / 1e4))} this month` +
    ` · ${tenthCents(s.estimates.autocomplete)} a search, ${tenthCents(s.estimates.details)} a pick`
  );
};

/** The lead picker's own lines (CAY-124, CAY-120 finding 4; widths measured at 390px). */
export const PICKER_COPY = {
  noMatch: (query: string) => `No match for "${query}" — try the street or city.`,
  offline: 'No signal — search resumes when it is back. Adding it manually below works offline.',
  unavailable: 'Place search unavailable — add it manually below.',
} as const;

/** Why the D110 picker is off, in words: the server's `reason` is an enum, never shown raw.
 *  A $0 cap is switched off by configuration, so "until next month" would not be true. */
export const pickerOffLabel = (s: { reason: string; cap_micros: number }): string => {
  if (s.reason === 'cap_reached' && s.cap_micros > 0)
    return `Place search is off until next month ($${(Math.floor(s.cap_micros / 1e4) / 100).toFixed(2)} cap) — add the address manually.`;
  if (s.reason === 'cap_reached' || s.reason === 'not_configured' || s.reason === 'invalid_cap')
    return 'Place search is not set up — add the address manually.';
  return PICKER_COPY.unavailable;
};
